<template>
  <div class="plm-ai-page">
    <s-r-m-header activeVal="plmAi" />

    <nav class="floating-nav" :class="{ visible: showFloatingNav }">
      <div class="floating-nav-card">
        <ul>
          <li
            v-for="item in floatingNavItems"
            :key="item.id"
            :class="{ active: activeNavSection === item.id }"
            @click="scrollTo(item.id)"
          >
            <span class="nav-dot"></span>
            <span class="nav-label">{{ item.label }}</span>
          </li>
        </ul>
      </div>
    </nav>

    <main>
      <section
        class="hero-process"
        :style="{ backgroundImage: heroBackground }"
      >
        <div class="hero-shade">
          <div class="wrap process-hero-grid">
            <div class="hero-copy">
              <h1>
                <span class="title-main">配方研发</span>
                <span class="title-accent">从“试出来”走向“算出来”</span>
              </h1>
              <div class="hero-title-line"></div>
              <p class="lead">
                璞华易研PLM内置贝叶斯优化 + AI推荐引擎，让配方研发更加简单高效
              </p>
              <ul class="hero-tags">
                <li v-for="item in moduleCards" :key="item.id">
                  {{ item.title }}
                </li>
              </ul>
              <button class="primary-btn large" type="button" @click="toDemo">
                <span>预约演示</span>
                <el-image :src="linkIcon" alt="" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <section class="section" id="ai-assistant">
        <div class="wrap narrow">
          <div class="section-head">
            <span class="section-kicker">AI Assistant</span>
            <h2>AI助手：让研发数据“开口说话”</h2>
            <p>
              璞华易研AI智能体深度融入PLM系统，实时读取物料、配方、实验、项目、文档等模块的结构化与非结构化数据。无论是撰写报告、翻译文献、对比文件，还是分析实验数据，只需一句话指令，AI即刻响应。
            </p>
          </div>

          <div class="journey">
            <article
              v-for="(step, index) in assistantSteps"
              :key="step.title"
              class="module-card journey-step"
              :class="{ reverse: index % 2 === 1 }"
            >
              <div class="journey-media">
                <mobile-tab-fold
                  :tabs="toFoldTabs(step.images)"
                  :value="String(assistantActive[index])"
                  @input="val => setAssistantImage(index, Number(val))"
                />
                <div class="image-title-tabs">
                  <button
                    v-for="(image, imageIndex) in step.images"
                    :key="image.title"
                    type="button"
                    :class="{ active: assistantActive[index] === imageIndex }"
                    @click="setAssistantImage(index, imageIndex)"
                  >
                    {{ image.title }}
                  </button>
                </div>
                <el-image
                  class="screen"
                  fit="contain"
                  :src="step.images[assistantActive[index]].src"
                  :alt="step.images[assistantActive[index]].alt"
                  :preview-src-list="step.images.map((image) => image.src)"
                />
              </div>
              <div class="journey-copy">
                <span class="stat-num step-num">{{ step.index }}</span>
                <h3>{{ step.title }}</h3>
                <p>{{ step.description }}</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section class="section blue" id="bayes">
        <div class="wrap">
          <div class="section-head light">
            <span class="section-kicker">EOBO</span>
            <h2 class="bayes-title">
              <span>AI贝叶斯优化（EOBO）：专为配方型产业打造，</span>
              <span>让每一次实验都离最优配方更近一步</span>
            </h2>
            <p>
              璞华易研PLM内置贝叶斯优化引擎，通过基于高斯过程与“探索-开发-平衡“策略，用最少实验次数逼近最优解。
            </p>
          </div>

          <div class="bayes-tabs">
            <mobile-tab-fold
              :tabs="bayesFoldTabs"
              :value="String(activeBayesTab)"
              @input="val => activeBayesTab = Number(val)"
            />
            <div
              class="bayes-tab-list"
              role="tablist"
              aria-label="贝叶斯优化能力"
            >
              <button
                v-for="(tab, index) in bayesTabs"
                :key="tab.title"
                type="button"
                role="tab"
                :aria-selected="activeBayesTab === index"
                :class="{ active: activeBayesTab === index }"
                @click="activeBayesTab = index"
              >
                <span>{{ formatIndex(index + 1) }}</span>
                {{ tab.title }}
              </button>
            </div>
            <article
              v-for="(tab, index) in bayesTabs"
              v-show="activeBayesTab === index"
              :key="tab.title"
              class="bayes-panel"
            >
              <div class="bayes-panel-copy">
                <h3>{{ tab.title }}</h3>
                <p>{{ tab.description }}</p>
              </div>
              <el-image
                class="screen"
                fit="contain"
                :src="tab.image"
                :alt="tab.alt"
                :preview-src-list="bayesTabs.map((item) => item.image)"
              />
            </article>
          </div>
        </div>
      </section>

      <section class="section soft" id="formula">
        <div class="wrap">
          <div class="section-head">
            <span class="section-kicker">Formula Recommendation</span>
            <h2 class="formula-title">
              <span> 两大AI引擎，驱动配方创新 </span>
              <span>
                <span class="term-note">
                  LLM
                  <el-tooltip
                    effect="light"
                    placement="top"
                    popper-class="term-tooltip-popper"
                  >
                    <i class="term-note-icon">!</i>
                    <div slot="content">
                      LLM 是 Large Language Model（大语言模型）
                    </div>
                  </el-tooltip>
                </span>
                通用探索 +
                <span class="term-note">
                  ML
                  <el-tooltip
                    effect="light"
                    placement="top"
                    popper-class="term-tooltip-popper"
                  >
                    <i class="term-note-icon">!</i>
                    <div slot="content">ML 是 Machine Learning（机器学习）</div>
                  </el-tooltip>
                </span>
                /
                <span class="term-note">
                  DL
                  <el-tooltip
                    effect="light"
                    placement="top"
                    popper-class="term-tooltip-popper"
                  >
                    <i class="term-note-icon">!</i>
                    <div slot="content">DL 是 Deep Learning（深度学习）</div>
                  </el-tooltip>
                </span>
                精准进化
              </span>
            </h2>
            <p>
              璞华易研PLM同时搭载「大语言模型」与「机器学习/深度学习」双引擎。LLM融合全网文献与内部知识，快速生成配方方案；ML/DL基于企业真实历史数据自训练，持续迭代、越用越准。通用能力与专属智能，一次拥有。
            </p>
          </div>

          <div class="support-grid">
            <article
              v-for="item in supportList"
              :key="item.title"
              class="support-item"
            >
              <h3>{{ item.title }}</h3>
              <p>{{ item.description }}</p>
            </article>
          </div>

          <div class="engine-lanes">
            <article
              v-for="(engine, index) in engineList"
              :key="engine.title"
              class="lane"
              :class="{ 'lane-reverse': index === 1 }"
            >
              <div class="lane-copy">
                <h3>{{ engine.title }}</h3>
                <p>{{ engine.description }}</p>
                <ul class="feature-list">
                  <li v-for="item in engine.features" :key="item.title">
                    <strong>{{ item.title }}</strong>
                    <template v-if="item.term === 'xgboost'">
                      从特征工程、模型选型到
                      <span class="term-note">
                        XGBoost
                        <el-tooltip
                          effect="light"
                          placement="top"
                          popper-class="term-tooltip-popper"
                        >
                          <i class="term-note-icon">!</i>
                          <div slot="content">
                            XGBoost（eXtreme Gradient
                            Boosting）是一种高效、灵活且可扩展的梯度提升决策树（Gradient
                            Boosted Decision
                            Trees）算法实现，属于传统机器学习（ML）中的监督学习模型，主要用于表格型数据（结构化数据）。
                          </div>
                        </el-tooltip>
                      </span>
                      等算法和超参数优化，全部自主研发。
                    </template>
                    <template v-else>{{ item.description }}</template>
                  </li>
                </ul>
              </div>
              <div class="engine-showcase">
                <mobile-tab-fold
                  :tabs="toFoldTabs(engine.images)"
                  :value="String(engine.active)"
                  @input="val => engine.active = Number(val)"
                />
                <div class="image-title-tabs compact">
                  <button
                    v-for="(image, imageIndex) in engine.images"
                    :key="image.title"
                    type="button"
                    :class="{ active: engine.active === imageIndex }"
                    @click="engine.active = imageIndex"
                  >
                    {{ image.title }}
                  </button>
                </div>
                <el-image
                  class="screen"
                  fit="contain"
                  :src="engine.images[engine.active].src"
                  :alt="engine.images[engine.active].alt"
                  :preview-src-list="engine.images.map((image) => image.src)"
                />
              </div>
            </article>
          </div>
        </div>
      </section>

      <section class="section" id="spectrum">
        <div class="wrap narrow">
          <div class="section-head">
            <span class="section-kicker">Chemical Spectrum</span>
            <h2>
              化学图谱智能分析：从谱图到结构，不仅懂“是什么”，更懂“为什么”
            </h2>
            <p>
              璞华易研PLM内置AI波谱学引擎，融合
              <span class="term-note">
                GNN分子表征
                <el-tooltip
                  effect="light"
                  placement="top"
                  popper-class="term-tooltip-popper"
                >
                  <i class="term-note-icon">!</i>
                  <div slot="content">
                    GNN分子表征指的是：使用图神经网络（Graph Neural
                    Network）对分子结构进行学习，从而生成能反映分子物理化学性质的向量表示（嵌入）
                  </div>
                </el-tooltip>
              </span>
              、CNN/Transformer谱图特征提取与化学大语言模型。支持
              <span class="term-note">
                GC-MS
                <el-tooltip
                  effect="light"
                  placement="top"
                  popper-class="term-tooltip-popper"
                >
                  <i class="term-note-icon">!</i>
                  <div slot="content">GC-MS（气相色谱-质谱联用仪）</div>
                </el-tooltip>
              </span>
              、
              <span class="term-note">
                NMR
                <el-tooltip
                  effect="light"
                  placement="top"
                  popper-class="term-tooltip-popper"
                >
                  <i class="term-note-icon">!</i>
                  <div slot="content">NMR（核磁共振波谱）</div>
                </el-tooltip>
              </span>
              、
              <span class="term-note">
                IR
                <el-tooltip
                  effect="light"
                  placement="top"
                  popper-class="term-tooltip-popper"
                >
                  <i class="term-note-icon">!</i>
                  <div slot="content">IR（红外光谱）</div>
                </el-tooltip>
              </span>
              、
              <span class="term-note">
                Raman
                <el-tooltip
                  effect="light"
                  placement="top"
                  popper-class="term-tooltip-popper"
                >
                  <i class="term-note-icon">!</i>
                  <div slot="content">Raman（拉曼光谱）</div>
                </el-tooltip>
              </span>
              等多谱图联合分析，全流程自动解析化合物结构式或组分比例，并提供自然语言证据链报告，让波谱分析告别“黑箱“。
            </p>
          </div>
          <div class="spectrum-showcase">
            <div class="spectrum-copy-panel">
              <ul class="feature-list numbered">
                <li v-for="item in spectrumFeatures" :key="item.title">
                  <strong>{{ item.title }}</strong>
                  {{ item.description }}
                </li>
              </ul>
            </div>
            <div class="spectrum-visual-panel">
              <mobile-tab-fold
                :tabs="spectrumFoldTabs"
                :value="String(activeSpectrumImage)"
                @input="val => activeSpectrumImage = Number(val)"
              />
              <div class="image-title-tabs">
                <button
                  v-for="(image, imageIndex) in spectrumImages"
                  :key="image.title"
                  type="button"
                  :class="{ active: activeSpectrumImage === imageIndex }"
                  @click="activeSpectrumImage = imageIndex"
                >
                  {{ image.title }}
                </button>
              </div>
              <el-image
                class="screen"
                fit="contain"
                :src="spectrumImages[activeSpectrumImage].src"
                :alt="spectrumImages[activeSpectrumImage].alt"
                :preview-src-list="spectrumImages.map((image) => image.src)"
              />
            </div>
          </div>
        </div>
      </section>

      <section class="section soft" id="case-showcase">
        <div class="wrap">
          <div class="section-head">
            <span class="section-kicker">AI Industry Applications</span>
            <h2>AI行业应用</h2>
            <p>
              覆盖化工新材料、日化美妆、食品饮料、生物医药四大流程行业的AI+PLM解决方案，深度结合行业特性与合规要求，驱动研发提质增效。
            </p>
          </div>

          <div class="industry-tabs">
            <mobile-tab-fold
              :tabs="industryFoldTabs"
              :value="String(activeIndustry)"
              @input="val => activeIndustry = Number(val)"
            />
            <div class="industry-tab-list" role="tablist" aria-label="行业应用">
              <button
                v-for="(ind, index) in aiIndustryData"
                :key="ind.name"
                type="button"
                role="tab"
                :aria-selected="activeIndustry === index"
                :class="{ active: activeIndustry === index }"
                @click="activeIndustry = index"
              >
                <span>{{ formatIndex(index + 1) }}</span>
                <strong>{{ ind.name }}</strong>
                <small>{{ ind.scene }}</small>
              </button>
            </div>

            <article
              v-for="(ind, index) in aiIndustryData"
              v-show="activeIndustry === index"
              :key="ind.name"
              class="industry-panel"
            >
              <div class="industry-body">
                <div class="industry-traits">
                  <h3>行业特性</h3>
                  <p>{{ ind.traits }}</p>
                </div>

                <div class="industry-detail">
                  <div class="industry-section industry-scenarios">
                    <h3>
                      PLM+AI应用场景
                      <span v-if="ind.products" class="industry-products-tag">
                        {{ ind.products }}
                      </span>
                    </h3>
                    <div class="scenario-list">
                      <div
                        v-for="sc in ind.scenarios"
                        :key="sc.title"
                        class="scenario-card"
                      >
                        <h4>{{ sc.title }}</h4>
                        <p>{{ sc.description }}</p>
                      </div>
                    </div>
                  </div>
                  <div class="industry-media">
                    <div class="industry-image-card">
                      <el-image
                        class="screen"
                        fit="contain"
                        :src="ind.image"
                        :alt="ind.imageAlt"
                        :preview-src-list="
                          aiIndustryData.map((item) => item.image)
                        "
                      />
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section class="section" id="research">
        <div class="wrap">
          <div class="section-head">
            <span class="section-kicker">Research Collaboration</span>
            <h2>AI 算法源自顶尖科研合作</h2>
            <p>
              璞华易研 PLM
              深度联动武汉大学、武汉理工大学、北京大学、武汉人工智能研究院等顶尖高校与科研机构，构建产学研一体化
              AI 创新生态。我们依托高校前沿算法研究能力，以10 万 +
              真实配方数据、百万级文献与专利库为基础训练工业级 AI
              模型，持续推进模型优化与落地应用。
            </p>
          </div>
          <div class="partner-grid">
            <div
              v-for="partner in partners"
              :key="partner.name"
              class="partner"
              :style="{ '--partner-logo-scale': partner.scale }"
            >
              <el-image
                fit="contain"
                :src="partner.logo"
                :alt="partner.name"
                :preview-src-list="partners.map((item) => item.logo)"
              />
            </div>
          </div>
        </div>
      </section>

      <section class="section soft" id="faq">
        <div class="wrap">
          <div class="section-head">
            <span class="section-kicker">FAQ</span>
            <h2>常见问题</h2>
            <p>
              围绕AI助手、贝叶斯优化、配方推荐与化学图谱智能分析的常见问题。
            </p>
          </div>
          <div class="faq-grid">
            <article
              v-for="(item, index) in faqList"
              :key="item.question"
              class="faq"
            >
              <span class="faq-num">{{ formatIndex(index + 1) }}</span>
              <h3>{{ item.question }}</h3>
              <p>{{ item.answer }}</p>
            </article>
          </div>
        </div>
      </section>

      <custom-form />
    </main>

    <footer-box />
  </div>
</template>

<script>
import SRMHeader from '@/components/SRMHeader/index'
import CustomForm from '@/components/Form'
import FooterBox from '@/components/FooterBox'
import MobileTabFold from '@/components/MobileTabFold/index.vue'
import { linkMap } from '@/utils'

import linkIcon from '@/assets/images/link-to.png'
import banner from '@/assets/images/AI/banner.png'
import aiSearch1 from '@/assets/images/AI/ai-assistant-search-source.png'
import aiSearch2 from '@/assets/images/AI/ai-assistant-search-enhance.png'
import aiMap1 from '@/assets/images/AI/ai-assistant-parse.png'
import aiMap2 from '@/assets/images/AI/ai-assistant-map-fields.png'
import aiCompare1 from '@/assets/images/AI/ai-assistant-compare-file.png'
import aiCompare2 from '@/assets/images/AI/ai-assistant-compare-result.png'
import bayes1 from '@/assets/images/AI/bayes-optimization-navigation.png'
import bayes2 from '@/assets/images/AI/bayes-optimization-data.png'
import bayes3 from '@/assets/images/AI/bayes-optimization-result.png'
import llm1 from '@/assets/images/AI/llm-target-input.png'
import llm2 from '@/assets/images/AI/llm-plan-generation.png'
import llm3 from '@/assets/images/AI/llm-formula-analysis.png'
import llm4 from '@/assets/images/AI/llm-result-compare.png'
import ml1 from '@/assets/images/AI/ml-data-modeling.png'
import ml2 from '@/assets/images/AI/ml-model-training.png'
import ml3 from '@/assets/images/AI/ml-recommendation-process.png'
import ml4 from '@/assets/images/AI/ml-recommendation-result.png'
import spectrum1 from '@/assets/images/AI/spectrum-analysis.png'
import spectrum2 from '@/assets/images/AI/spectrum-report.png'
import whu from '@/assets/images/AI/partner-wuhan-university.png'
import whut from '@/assets/images/AI/partner-wuhan-university-technology.png'
import pku from '@/assets/images/AI/partner-peking-university.png'
import aiInstitute from '@/assets/images/AI/partner-wuhan-ai-institute.png'
import industryBeauty from '@/assets/images/AI/industry-1.png'
import industryFood from '@/assets/images/AI/industry-2.png'
import industryMedical from '@/assets/images/AI/industry-3.png'
import industryChemical from '@/assets/images/AI/industry-4.png'

import { baiduConsult } from './common.js'

export default {
  name: 'PLMAI',
  components: {
    SRMHeader,
    CustomForm,
    FooterBox,
    MobileTabFold,
  },
  metaInfo() {
    const url = 'https://plm.purvar.com/PLMAI'
    const title = 'PLM+AI配方研发系统_AI贝叶斯优化_EOBO配方推荐_璞华易研'
    const description =
      '璞华易研PLM+AI配方研发系统，集成AI助手、AI贝叶斯优化EOBO、LLM/ML配方推荐和化学图谱智能分析，帮助化工新材料、日化美妆、食品饮料、生物医药企业提升研发效率。'
    return {
      title,
      meta: [
        {
          name: 'robots',
          content: 'index,follow',
        },
        {
          name: 'keywords',
          content:
            'PLM+AI,AI PLM,配方研发系统,AI配方推荐,AI贝叶斯优化,EOBO,贝叶斯优化,配方推荐,LLM配方推荐,ML配方推荐,AI助手,化学图谱智能分析,研发数据智能平台,化工新材料PLM,日化美妆PLM,食品饮料PLM,生物医药PLM,璞华易研PLM',
        },
        {
          name: 'description',
          content: description,
        },
        {
          property: 'og:type',
          content: 'website',
        },
        {
          property: 'og:title',
          content: title,
        },
        {
          property: 'og:description',
          content: description,
        },
        {
          property: 'og:url',
          content: url,
        },
        {
          property: 'og:site_name',
          content: '璞华易研PLM',
        },
        {
          property: 'og:image',
          content: 'https://plm.purvar.com/favicon.jpg',
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:title',
          content: title,
        },
        {
          name: 'twitter:description',
          content: description,
        },
      ],
    }
  },
  data() {
    const faqList = [
      {
        question: 'AI助手与传统的PLM搜索有什么本质区别？',
        answer:
          '传统搜索仅匹配字面关键词，AI助手具备语义理解能力，能基于同义词、业务隐含条件及跨模块关联识别用户真实意图。它可以直接输出答案、依据和建议，并附原始信息来源，而非仅返回文件列表。',
      },
      {
        question: '贝叶斯优化（EOBO）如何帮助配方研发减少实验次数？',
        answer:
          '贝叶斯优化采用“边实验边学习”的策略，每次实验完成后更新模型，下一轮自动推荐更有价值的参数组合。例如8因素配方优化场景，传统方法需要大量实验，而EOBO通常在数十次实验内即可锁定最优区间，显著降低试错成本。',
      },
      {
        question: 'LLM配方推荐和ML/DL配方推荐有什么区别？我该如何选择？',
        answer:
          '两者是互补的双引擎。LLM融合全网文献、专利与内部知识，适合快速生成多种可行方案、启发创新；ML/DL基于企业自身历史配方与实验数据训练，持续迭代、越用越准，适合在已有经验基础上精确优化。两者可同时使用，通用能力与专属智能一次拥有。',
      },
      {
        question: 'AI推荐的物料是否可直接用于采购和生产？',
        answer:
          '可以。系统会自动匹配PLM中的物料主数据，并同步校验合规性，确保推荐的原料可采购、可生产且符合法规要求。确认后还可一键生成配方单、实验单，直接进入研发工作流。',
      },
      {
        question: '化学图谱智能分析能处理哪些类型的谱图？能否给出结构式？',
        answer:
          '支持GC-MS、NMR、IR、Raman等多种谱图，并可联合分析。系统能够自动完成基线校正、峰识别、谱库匹配，并输出化合物结构式或组分比例，同时提供置信度评分和关键谱峰归属解释。',
      },
      {
        question: 'ML/DL配方推荐的模型是否依赖外部API？数据安全如何保障？',
        answer:
          '不依赖任何第三方模型API。从特征工程、模型选型到超参数优化，全部采用自研算法。同时支持完全私有化部署，模型训练与推理全在企业内网完成，核心配方数据无需上传外网。',
      },
      {
        question: 'AI助手能否自动从PDF、图片中提取关键信息并填入系统？',
        answer:
          '可以。上传PDF、Word、图片或扫描件后，AI会自动提取关键信息，并一键写入PLM对应字段，大幅替代手工录入，提升效率。',
      },
      {
        question: '贝叶斯优化如何应对多个冲突目标？',
        answer:
          'EOBO内置帕累托前沿搜索，一次运行可输出一组非支配解，例如“成本95元/性能90分”和“成本100元/性能95分”等多个方案，供研发人员根据实际偏好权衡决策，而非强制给出单一答案。',
      },
      {
        question: '化学图谱分析生成的报告能否直接用于论文或申报资料？',
        answer:
          '可以。系统基于大语言模型自动生成结构化、可审核的专业分析报告，包含峰位解释、证据链溯源、结构可行性评估与置信度评分，支持PDF/Word一键导出。',
      },
      {
        question: '模型是否会随着我司实验数据的积累而变聪明？',
        answer:
          '是的。ML/DL配方推荐引擎支持持续增量学习，每完成一次实验，结果自动回流模型，模型每周增量更新。随着真实实验数据的不断积累，推荐准确率会稳步提升。',
      },
    ]

    return {
      linkMap,
      linkIcon,
      banner,
      floatingNavItems: [
        { id: 'ai-assistant', label: 'AI助手' },
        { id: 'bayes', label: '贝叶斯优化' },
        { id: 'formula', label: '配方推荐' },
        { id: 'spectrum', label: '化学图谱' },
        { id: 'case-showcase', label: '行业应用' },
        { id: 'faq', label: '常见问题' },
      ],
      activeNavSection: 'ai-assistant',
      showFloatingNav: false,
      assistantActive: [0, 0, 0],
      activeBayesTab: 0,
      faqList,
      moduleCards: [
        {
          id: 'ai-assistant',
          index: '01',
          title: 'AI助手',
          description: '让研发数据“开口说话”。',
        },
        {
          id: 'bayes',
          index: '02',
          title: 'AI贝叶斯优化',
          description: '让每一次实验都离最优配方更近一步。',
        },
        {
          id: 'formula',
          index: '03',
          title: '配方推荐',
          description: '两大AI引擎，驱动配方智能创新。',
        },
        {
          id: 'spectrum',
          index: '04',
          title: '化学图谱智能分析',
          description: '从谱图到结构，不仅“是什么”，更懂“为什么”。',
        },
      ],
      assistantSteps: [
        {
          index: '01',
          title: '增强搜索：不止是关键词匹配',
          description:
            '传统搜索仅匹配字面关键词，AI助手可进行语义理解，如：可基于同义词、业务隐含条件及跨模块关联等识别用户真实意图。AI基于系统内全量关联数据，直接输出答案、依据和建议，并附原始信息来源。',
          images: [
            { title: '信息来源', src: aiSearch1, alt: 'AI助手信息来源' },
            { title: '增强搜索', src: aiSearch2, alt: 'AI助手增强搜索' },
          ],
        },
        {
          index: '02',
          title: 'AI解析与自动提取：让非结构化数据变资产',
          description:
            '上传PDF、Word、图片、扫描件，AI自动提取关键信息（如：物性数据、合同条款、化合物名称等），一键写入PLM对应字段，替代手工录入，大幅提升效率。',
          images: [
            { title: 'AI解析', src: aiMap1, alt: 'AI解析映射' },
            { title: '字段映射', src: aiMap2, alt: 'AI字段映射' },
          ],
        },
        {
          index: '03',
          title: '文件内容对比：差异一目了然',
          description:
            '上传两份配方文件、实验报告或新旧标准，AI自动比对并生成差异化摘要（修改参数、新增章节、删除数据等），支持高亮标注与对比报告导出。',
          images: [
            { title: '文件对比', src: aiCompare1, alt: 'AI文件对比' },
            { title: '差异摘要', src: aiCompare2, alt: 'AI对比结果' },
          ],
        },
      ],
      bayesTabs: [
        {
          title: '实验导航，高效迭代',
          description:
            '样本效率优异，大幅降低实验试错频次。传统方法需要预设大量实验组合，贝叶斯优化边实验边学习，单次实验完成即更新模型，下一轮推荐更具价值。例如8因素配方优化场景，传统设计往往需要大量实验，而贝叶斯优化通常可在数十次实验内锁定最优区间。',
          image: bayes1,
          alt: '贝叶斯优化实验导航',
        },
        {
          title: '多目标协同优化',
          description:
            '配方研发常面临“成本最低、性能最高、合规最严”等多目标权衡。贝叶斯优化内置帕累托前沿搜索，一次运行输出一组非支配解（如“成本95元/性能90分”“成本100元/性能95分”），供研发人员决策。',
          image: bayes2,
          alt: '贝叶斯优化实验数据',
        },
        {
          title: '自带不确定性量化，决策有依据',
          description:
            '代理模型不仅输出预测值，还给出预测方差（不确定性）。低不确定性区域可放心开发，高不确定性区域提示需要进一步探索。结果可视化展示，帮助研发判断推荐值的可靠性。',
          image: bayes3,
          alt: '贝叶斯优化推荐结果',
        },
      ],
      supportList: [
        {
          title: '原材料自动匹配，合规校验',
          description:
            'AI自动匹配PLM系统中的物料主数据，同步校验合规性，确保推荐原料可采购、可生产、可合规。',
        },
        {
          title: '配方实验，一键落地',
          description:
            '推荐方案确认后，一键生成配方单、实验单，自动关联项目工作流，从灵感迸发到研发执行大幅缩短手工录入时间。',
        },
        {
          title: '寻源 & 测评，入库全链路',
          description:
            '辅助新材料寻源，智能推荐潜在原料并提取技术指标；支持技术测评流程线上化，测评通过后一键入库。',
        },
        {
          title: '私有化部署，数据不出厂',
          description:
            '模型训练与推理全在企业内网完成，核心配方数据无需上传外网，确保配方资产零外流。',
        },
      ],
      engineList: [
        {
          title: 'LLM配方推荐：从“经验”到“智能生成”',
          description:
            '璞华易研PLM内置成熟垂直语言模型，融合双数据源智能决策，从「全网前沿洞察」到「企业经验沉淀」，通过自然语言交互，快速生成多种可行配方方案。',
          active: 0,
          features: [
            {
              title: '自然语言交互，双源融合',
              description:
                '用户只需输入研发目标或约束条件，系统即可自动融合文献、专利、原料趋势、法规与企业内部经验数据。',
            },
            {
              title: '智能权衡与快速生成',
              description:
                'AI自动权衡前沿创新性与企业实战可行性，快速输出多个可选配方方案。',
            },
          ],
          images: [
            { title: '目标输入', src: llm1, alt: 'LLM配方推荐目标输入' },
            { title: '方案生成', src: llm2, alt: 'LLM配方推荐方案生成' },
            { title: '配方分析', src: llm3, alt: 'LLM配方推荐配方分析' },
            { title: '结果对比', src: llm4, alt: 'LLM配方推荐结果对比' },
          ],
        },
        {
          title: 'ML/DL配方推荐：专属模型，持续进化',
          description:
            '自研机器学习、深度学习算法，基于企业真实历史配方数据、实验反馈、工艺参数训练专属模型。',
          active: 0,
          features: [
            {
              title: '基于企业真实数据训练',
              description:
                '模型学会的是“您的原料、您的设备、您的工艺”下的最优规律。',
            },
            {
              title: '自研算法，无需外部API',
              term: 'xgboost',
            },
            {
              title: '持续增量学习',
              description:
                '每完成一次实验，结果自动回流模型，推荐准确率随时间稳步提升。',
            },
            {
              title: '多目标优化 + 不确定性量化',
              description:
                '同时优化成本、性能、合规等冲突目标，每次推荐附带置信区间。',
            },
          ],
          images: [
            { title: '数据建模', src: ml1, alt: 'ML配方推荐数据建模' },
            { title: '模型训练', src: ml2, alt: 'ML配方推荐模型训练' },
            { title: '推荐过程', src: ml3, alt: 'ML配方推荐过程' },
            { title: '推荐结果', src: ml4, alt: 'ML配方推荐结果' },
          ],
        },
      ],
      spectrumImages: [
        { title: '谱图智能分析', src: spectrum1, alt: '谱图智能分析' },
        { title: '谱图分析报告', src: spectrum2, alt: '谱图分析报告' },
      ],
      activeSpectrumImage: 0,
      spectrumFeatures: [
        {
          title: '全流程自动解析',
          description:
            '从原始谱图输入到化合物结构式或组分比例，系统自动完成基线校正、峰识别、谱库匹配、结构生成。',
        },
        {
          title: '多谱图联合分析',
          description:
            '支持GC-MS、NMR、IR、Raman等多种谱图数据的联合分析与交叉验证。',
        },
        {
          title: '深度波谱学推理',
          description:
            '深度理解谱峰位移、裂分模式、耦合常数与化学基团、官能团、连接方式的对应关系。',
        },
        {
          title: '高置信度结果 + 专家审核闭环',
          description:
            '输出结构预测结果时附带置信度评分、关键谱峰归属解释、可能的结构异构体列表。',
        },
        {
          title: '报告生成引擎“用自然语言解释结果”',
          description:
            '自动生成结构化、可审核的专业分析报告，并支持 PDF/Word 一键导出。',
        },
      ],
      partners: [
        { name: '武汉大学', logo: whu, scale: 1.28 },
        { name: '武汉理工大学', logo: whut, scale: 1.58 },
        { name: '北京大学', logo: pku, scale: 1.58 },
        { name: '武汉人工智能研究院', logo: aiInstitute, scale: 1.64 },
      ],
      activeIndustry: 0,
      aiIndustryData: [
        {
          name: '化工新材料行业',
          image: industryChemical,
          imageAlt: '化工新材料AI应用',
          scene: '配方寻优 / 合规替代',
          products: 'AI贝叶斯优化（EOBO） + AI助手',
          traits:
            '化工新材料（如高性能树脂、特种涂料、电子化学品、可降解材料）的研发呈现高维度、非线性、强约束三大特征。一个配方常涉及8~15个组分变量（催化剂比例、反应温度、时间、压力等），且各因素间存在复杂的交互效应，传统单因素实验或正交设计往往需要上百次试错。同时，新材料面临全球合规高压——欧盟REACH、RoHS、中国GB标准对限用物质、毒理数据要求严苛，一个违规项可能导致整批产品下架。',
          scenarios: [
            {
              title: '场景1：高性能复合材料配方快速寻优',
              description:
                '某碳纤维预浸料企业需要平衡"拉伸强度＞1200MPa""固化时间＜90min""原料成本＜35元/kg"三个冲突目标。传统方法需64次DOE实验。AI贝叶斯优化：系统基于历史20次实验数据构建高斯过程代理模型，主动推荐第21~35次实验参数组合；每次实验后模型实时更新，不确定性降低，推荐逐步收敛至帕累托前沿；最终仅用37次实验即找到最优解，实验次数减少42%，研发周期从3个月压缩至1.5个月。',
            },
            {
              title: '场景2：法规驱动的替代材料智能推荐',
              description:
                '受欧盟微塑料禁令影响，某企业需要为磨砂洗面奶中的PE微珠寻找生物可降解替代物。AI助手：自动扫描内部PLM物料库和外部ChemWatch法规库，匹配出"改性纤维素"等3种候选；化学图谱智能分析（知识图谱）预测其与现有表活体系的相容性及磨砂粒径分布；一键生成替代实验方案，并自动更新合规报告，合规审核时间从2周缩短至2小时。',
            },
          ],
        },
        {
          name: '日化美妆行业',
          image: industryBeauty,
          imageAlt: '日化美妆AI应用',
          scene: '爆品快研 / 稳定归因',
          products: 'LLM配方推荐引擎 + 贝叶斯优化 + AI助手 + ML/DL配方推荐',
          traits:
            '日化美妆行业呈现"快、多、严、卷"四重挑战。快：新品从概念到上架普遍要求6~8周，头部品牌每周迭代2~3个SKU；多：成分表动辄30+种原料，且天然提取物批次稳定性差；严：《化妆品监督管理条例》要求功效宣称必须附有人体/体外实验证据，安全评估报告需完整毒理数据；卷：消费者通过小红书、抖音反向定制"早C晚A""仿生皮脂"等概念，要求品牌72小时内响应趋势。',
          scenarios: [
            {
              title: '场景1：热点成分快速配方生成',
              description:
                '某品牌产品经理输入需求："开发一款含积雪草发酵产物的舒缓精华，预算成本≤45元/30ml，肤感清爽，需通过人体斑贴测试。"LLM配方推荐引擎：融合全网文献（积雪草苷抗炎机制）、专利（发酵工艺优化）、竞品成分表（某大牌精华中积雪草添加量2.3%），以及企业内部基础配方库，自动生成3套完整方案；多目标贝叶斯优化：以成本、肤感黏度、细胞存活率（修复功效指标）为优化目标，推荐第1轮实验参数（积雪草提取物浓度、增稠剂比例、pH值）；AI小助手合规自检：AI自动比对《已使用化妆品原料目录》，预警"积雪草提取物"最大允许浓度，并生成备案所需的功效摘要模板。',
            },
            {
              title: '场景2：批次稳定性智能归因与调整',
              description:
                '某面霜连续三批出现"离心分层"，人工排查耗时2周未果。ML/DL模型自动关联批次记录：原料批号、均质转速、冷却曲线、灌装温度等532个数据点，识别出"乳化后冷却速度＞5℃/min"是主要归因。',
            },
          ],
        },
        {
          name: '食品饮料行业',
          image: industryFood,
          imageAlt: '食品饮料AI应用',
          scene: '风味优化 / 成本合规',
          products: 'LLM配方推荐引擎 + 贝叶斯优化 + AI助手 + ML/DL配方推荐',
          traits:
            '食品饮料研发面临三大刚性约束：成本（原料价格受期货市场波动，每月需更新报价）；风味（消费者感官评价主观且非线性，酸度增0.1%可能导致接受度从80%跌至40%）；健康（减糖、减脂、清洁标签趋势下，需找到功能与口感的平衡）。此外，需严格遵守GB 2760食品添加剂使用范围及限量，以及过敏原、营养标签强制标识。',
          scenarios: [
            {
              title: '场景1：低糖酸奶配方多目标优化',
              description:
                '某乳企计划开发一款"减糖50%但甜感接近全糖"的酸奶。传统方法依赖调香师反复尝试，周期长且成功率低。LLM知识挖掘：从3000+篇文献中提取赤藓糖醇、甜菊糖苷、罗汉果甜苷的协同增效曲线及后苦味掩盖方案；贝叶斯优化实验导航：以"甜度评分≥8.5/10""成本≤0.8元/杯""粘度≤12000cP"为目标，仅用28次实验（传统DOE需90次）找到最优组合：赤藓糖醇1.2%+甜菊糖苷0.03%+海藻糖0.5%；风味稳定性预测：基于微生物代谢模型，AI预测6个月货架期内甜度衰减曲线＜5%。',
            },
            {
              title: '场景2：原料短缺下的快速替代寻源',
              description:
                '某饼干厂急需寻找替代油脂（棕榈油、牛油果油等）并保持"酥脆度"和"起酥性"。AI助手：扫描PLM物料库及供应商数据库，筛选出4种物理化学指标接近的替代品；ML/DL配方推荐：基于历史配方中油脂替换实验记录，ML/DL模型预测替代后饼干的延展率、断裂力。推荐最佳替代油脂（分提棕榈油），并自动生成新配方BOM及成本变化对比。',
            },
          ],
        },
        {
          name: '生物医药行业',
          image: industryMedical,
          imageAlt: '生物医药AI应用',
          scene: '处方优化 / 谱图确证',
          products: '贝叶斯优化 + 化学知识图谱 + ML/DL配方推荐',
          traits:
            '生物医药研发具有"长周期、高投入、严监管"的典型特征。一个小分子化药从发现到上市平均耗时10~15年，耗费超10亿美元，其中临床前研究阶段即产生百万级数据点。监管方面，FDA 21 CFR Part 11、NMPA《药品记录与数据管理规范》要求每一实验步骤、每一配方变更均留下可审计痕迹。同时，制剂开发（处方前研究、辅料相容性、溶出曲线）高度依赖经验，变量多且交互作用复杂。',
          scenarios: [
            {
              title: '场景1：难溶性药物固体分散体处方优化',
              description:
                '某创新药化合物生物利用度低（F<15%），需开发热熔挤出固体分散体。涉及聚合物类型（HPMCAS、PVPVA等）、载药量（20%~60%）、螺杆转速、温度区段等多个连续与离散变量。贝叶斯优化引擎：基于前15次挤出实验结果（溶出度、结晶度、残留溶剂），主动推荐第16~25次实验参数；对高载药区（55%~60%）给出宽置信区间，提示需更多探索；提供三组解："溶出度90%+载药量45%""溶出度85%+载药量58%""溶出度92%+载药量40%"，供制剂科学家决策。',
            },
            {
              title: '场景2：化学谱图AI解析辅助结构确证',
              description:
                '某仿制药企业在申报ANDA时，需提供杂质结构确证资料（含NMR、MS、IR图谱）。传统人工解谱耗时2周且易误判。化学知识图谱模块：自动导入原始图谱（JCAMP-DX格式），完成基线校正、峰识别；基于图神经网络（GNN）匹配谱库及深度学习预测碎片离子归属，输出最可能的杂质结构（置信度96%）；提供3种异构体可能性及关键差异峰（如δ 2.1 vs 2.5）；支持专家在线修正，修正记录自动触发模型再训练。',
            },
            {
              title: '场景3：临床阶段制剂变更对溶出的影响预测',
              description:
                '在III期临床阶段，某原料药供应商变更导致晶型稍有差异，需评估对缓释片溶出曲线的影响。ML/DL配方推荐引擎：自动提取过去5年该品种所有批次的原料粒度分布、辅料水分、压片力、溶出数据（共1200+条）训练模型；输入新原料参数，预测4个时间点（1h/2h/4h/8h）溶出度及f2相似因子；输出结论：f2=68＞50，变更可接受；同时建议将压片力从12kN微调至11.5kN以更接近原研曲线。',
            },
          ],
        },
      ],
    }
  },
  computed: {
    heroBackground() {
      return `url(${this.banner})`
    },
    bayesFoldTabs() {
      return this.toFoldTabs(this.bayesTabs)
    },
    spectrumFoldTabs() {
      return this.toFoldTabs(this.spectrumImages)
    },
    industryFoldTabs() {
      return (this.aiIndustryData || []).map((item, index) => ({
        name: String(index),
        label: item.name,
      }))
    },
  },
  watch: {
    '$route.params.section'() {
      this.scrollToRouteSection()
    },
  },
  mounted() {
    baiduConsult()
    this.setCanonical()
    this.injectStructuredData()
    this.initFloatingNav()
    this.initRouteSectionScroll()
    this.scrollToRouteSection()
  },
  beforeDestroy() {
    this.removeStructuredData()
    this.restoreCanonical()
    this.destroyFloatingNav()
    this.destroyRouteSectionScroll()
  },
  methods: {
    formatIndex(index) {
      return index < 10 ? `0${index}` : `${index}`
    },
    toFoldTabs(list) {
      return (list || []).map((item, index) => ({
        name: String(index),
        label: item.title,
      }))
    },
    toDemo() {
      window.open(this.linkMap.demonstrateUrl)
    },
    scrollTo(id) {
      const target = document.getElementById(id)
      if (!target) return
      const top = target.getBoundingClientRect().top + window.pageYOffset - 70
      window.scrollTo({ top, behavior: 'smooth' })
    },
    scrollToRouteSection() {
      const { section } = this.$route.params
      if (!section) return
      this.$nextTick(() => {
        window.setTimeout(() => {
          this.scrollTo(section)
        }, 80)
      })
    },
    initRouteSectionScroll() {
      this._onRouteSectionScroll = (event) => {
        const section = event.detail && event.detail.section
        if (section) {
          this.scrollTo(section)
        }
      }
      window.addEventListener('plm-ai-scroll', this._onRouteSectionScroll)
    },
    destroyRouteSectionScroll() {
      if (this._onRouteSectionScroll) {
        window.removeEventListener('plm-ai-scroll', this._onRouteSectionScroll)
        this._onRouteSectionScroll = null
      }
    },
    setAssistantImage(stepIndex, imageIndex) {
      this.$set(this.assistantActive, stepIndex, imageIndex)
    },
    initFloatingNav() {
      this._onScroll = this.onFloatingNavScroll.bind(this)
      window.addEventListener('scroll', this._onScroll, { passive: true })
      this.onFloatingNavScroll()
    },
    destroyFloatingNav() {
      if (this._onScroll) {
        window.removeEventListener('scroll', this._onScroll)
        this._onScroll = null
      }
    },
    onFloatingNavScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      this.showFloatingNav = scrollTop > 400
      const sections = this.floatingNavItems
        .map((item) => document.getElementById(item.id))
        .filter(Boolean)
      const offset = 120
      let current = this.floatingNavItems[0]?.id || ''
      for (let i = sections.length - 1; i >= 0; i--) {
        if (sections[i].offsetTop <= scrollTop + offset) {
          current = this.floatingNavItems[i].id
          break
        }
      }
      this.activeNavSection = current
    },
    injectStructuredData() {
      this.removeStructuredData()
      const script = document.createElement('script')
      script.id = 'plm-ai-structured-data'
      script.type = 'application/ld+json'
      script.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Organization',
            '@id': 'https://plm.purvar.com/#organization',
            name: '璞华科技',
            url: 'https://plm.purvar.com/',
            logo: 'https://plm.purvar.com/favicon.jpg',
            contactPoint: {
              '@type': 'ContactPoint',
              telephone: '4006-160-730',
              contactType: 'sales',
              areaServed: 'CN',
              availableLanguage: ['zh-CN'],
            },
          },
          {
            '@type': 'WebSite',
            '@id': 'https://plm.purvar.com/#website',
            url: 'https://plm.purvar.com/',
            name: '璞华易研PLM',
            publisher: {
              '@id': 'https://plm.purvar.com/#organization',
            },
            inLanguage: 'zh-CN',
          },
          {
            '@type': 'WebPage',
            '@id': 'https://plm.purvar.com/PLMAI#webpage',
            url: 'https://plm.purvar.com/PLMAI',
            name: 'PLM+AI配方研发系统_AI贝叶斯优化_EOBO配方推荐_璞华易研',
            description:
              '璞华易研PLM+AI配方研发系统，集成AI助手、AI贝叶斯优化EOBO、LLM/ML配方推荐和化学图谱智能分析，帮助配方型企业提升研发效率。',
            isPartOf: {
              '@id': 'https://plm.purvar.com/#website',
            },
            about: {
              '@id': 'https://plm.purvar.com/PLMAI#software',
            },
            inLanguage: 'zh-CN',
          },
          {
            '@type': 'SoftwareApplication',
            '@id': 'https://plm.purvar.com/PLMAI#software',
            name: '璞华易研PLM+AI配方研发系统',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            url: 'https://plm.purvar.com/PLMAI',
            image: 'https://plm.purvar.com/favicon.jpg',
            description:
              '面向化工新材料、日化美妆、食品饮料、生物医药等配方型行业的PLM+AI研发管理系统，提供AI助手、AI贝叶斯优化、LLM/ML配方推荐和化学图谱智能分析能力。',
            featureList: [
              'AI助手增强搜索、文档解析与文件对比',
              'AI贝叶斯优化EOBO实验导航与多目标优化',
              'LLM通用探索与ML/DL专属配方推荐',
              'GC-MS、NMR、IR、Raman等化学图谱智能分析',
              '私有化部署与企业研发数据安全管理',
            ],
            provider: {
              '@id': 'https://plm.purvar.com/#organization',
            },
            offers: {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'CNY',
              availability: 'https://schema.org/InStock',
              url: 'https://plm.purvar.com/PLMAI',
              description: '请联系璞华易研获取PLM+AI配方研发系统演示与报价。',
            },
          },
          {
            '@type': 'BreadcrumbList',
            '@id': 'https://plm.purvar.com/PLMAI#breadcrumb',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://plm.purvar.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'PLM + AI应用',
                item: 'https://plm.purvar.com/PLMAI',
              },
            ],
          },
          {
            '@type': 'FAQPage',
            '@id': 'https://plm.purvar.com/PLMAI#faq',
            mainEntity: this.faqList.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: item.answer,
              },
            })),
          },
        ],
      })
      document.head.appendChild(script)
    },
    removeStructuredData() {
      const script = document.getElementById('plm-ai-structured-data')
      if (script && script.parentNode) {
        script.parentNode.removeChild(script)
      }
    },
    setCanonical() {
      const canonical =
        document.querySelector('link[rel="canonical"]') ||
        document.createElement('link')
      if (!canonical.parentNode) {
        document.head.appendChild(canonical)
      }
      if (!canonical.dataset.originalHref) {
        canonical.dataset.originalHref = canonical.getAttribute('href') || ''
      }
      canonical.setAttribute('rel', 'canonical')
      canonical.setAttribute('href', 'https://plm.purvar.com/PLMAI')
      canonical.dataset.plmAiCanonical = 'true'
    },
    restoreCanonical() {
      const canonical = document.querySelector(
        'link[data-plm-ai-canonical="true"]',
      )
      if (!canonical) return
      const originalHref = canonical.dataset.originalHref
      if (originalHref) {
        canonical.setAttribute('href', originalHref)
        canonical.removeAttribute('data-plm-ai-canonical')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.plm-ai-page {
  color: #202636;
  background: #ffffff;

  button {
    font-family: inherit;
  }
}

.floating-nav {
  position: fixed;
  left: 2.4rem;
  top: 30%;
  transform: translateY(-50%);
  z-index: 80;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.3s ease;

  &.visible {
    opacity: 1;
    pointer-events: auto;
  }
}

.floating-nav-card {
  padding: 1.8rem 2rem;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(1.2rem);
  border: 0.1rem solid rgba(54, 116, 254, 0.12);
  border-radius: 0.6rem;
  box-shadow:
    0 0.8rem 3.2rem rgba(54, 116, 254, 0.08),
    0 0.2rem 0.8rem rgba(22, 52, 118, 0.06);

  ul {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    padding: 0.6rem 0.8rem;
    border-radius: 0.6rem;
    cursor: pointer;
    transition:
      background 0.2s ease,
      color 0.2s ease;

    &:hover {
      background: rgba(54, 116, 254, 0.05);
    }

    &.active {
      background: rgba(54, 116, 254, 0.08);

      .nav-dot {
        background: #3674fe;
        box-shadow: 0 0 0 0.4rem rgba(54, 116, 254, 0.2);
      }

      .nav-label {
        color: #3674fe;
      }
    }
  }

  .nav-dot {
    width: 1rem;
    height: 1rem;
    flex: 0 0 auto;
    border-radius: 50%;
    background: #d0d8e8;
    transition:
      background 0.2s ease,
      box-shadow 0.2s ease;
  }

  .nav-label {
    font-size: 1.6rem;
    font-weight: 600;
    color: #8b93a6;
    white-space: nowrap;
    transition: color 0.2s ease;
    user-select: none;
  }
}

.wrap {
  width: 142rem;
  max-width: calc(100% - 8rem);
  margin: 0 auto;

  &.narrow {
    width: 142rem;
  }
}

.hero-process {
  min-height: 82rem;
  background-size: cover;
  background-position: center;

  .hero-shade {
    min-height: 82rem;
    padding: 16rem 0 8rem;
  }
}

.process-hero-grid {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  min-height: 58rem;
}

.hero-copy {
  max-width: 66rem;

  h1 {
    margin: 0 0 3rem;
    color: #172033;
    font-size: 4.8rem;
    font-weight: 700;
    line-height: 1.34;

    span {
      display: block;
      white-space: nowrap;
    }

    .title-accent {
      color: #3674fe;
    }
  }

  .hero-title-line {
    width: 5.4rem;
    height: 0.4rem;
    margin-bottom: 4rem;
    background: linear-gradient(90deg, #0f81f4 0%, #37a4ff 100%);
    border-radius: 0.4rem;
  }

  .lead {
    width: 65rem;
    max-width: 65rem;
    margin: 0 0 2.4rem;
    color: #293244;
    font-size: 2.4rem;
    font-weight: 700;
    line-height: 1.8;
    white-space: nowrap;
  }

  .hero-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 1.8rem 3rem;
    margin: 0 0 6rem;
    padding: 0;
    list-style: none;

    li {
      position: relative;
      padding-left: 2.8rem;
      color: #293244;
      font-size: 1.8rem;
      font-weight: 700;
      line-height: 2.8rem;
      white-space: nowrap;

      &::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        width: 1.6rem;
        height: 1.6rem;
        border: 0.2rem solid #3674fe;
        border-radius: 50%;
        transform: translateY(-50%);
      }

      &::after {
        content: '';
        position: absolute;
        left: 0.5rem;
        top: 47%;
        width: 0.8rem;
        height: 0.4rem;
        border-left: 0.2rem solid #3674fe;
        border-bottom: 0.2rem solid #3674fe;
        transform: translateY(-62%) rotate(-45deg);
      }
    }
  }
}

.eyebrow,
.section-kicker {
  display: inline-flex;
  align-items: center;
  margin-bottom: 1.4rem;
  color: #3674fe;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.4rem;
  letter-spacing: 0;
  text-transform: uppercase;
}

.primary-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  // padding: 0 3rem;
  padding: 1.2rem 2.3rem;
  border: 0.1rem solid #ffffff;
  height: 5.6rem;
  color: #ffffff;
  font-size: 2rem;
  font-weight: 700;
  // border: 0;
  border-radius: 2.8rem;
  background: linear-gradient(318deg, #0f81f4 0%, #37a4ff 100%);
  box-shadow: 0 1.6rem 3.4rem rgba(54, 116, 254, 0.28);
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  // &:hover {
  //   transform: translateY(-0.2rem);
  //   box-shadow: 0 2rem 4rem rgba(54, 116, 254, 0.32);
  // }

  ::v-deep .el-image {
    width: 3rem;
    height: 3rem;
  }
}

.process-map {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.2rem;
  width: 54rem;
  max-width: 100%;
  margin-top: 2.8rem;
}

.module-card {
  background: #ffffff;
  box-shadow: 0 1.8rem 5rem rgba(88, 124, 169, 0.14);
}

.process-card {
  position: relative;
  min-height: 10rem;
  padding: 1.8rem 2rem;
  overflow: hidden;
  border: 0.1rem solid rgba(54, 116, 254, 0.2);
  background: rgba(255, 255, 255, 0.72);
  box-shadow:
    inset 0 0.1rem 0 rgba(255, 255, 255, 0.78),
    inset 0 0 0 0.1rem rgba(255, 255, 255, 0.18),
    0 1.2rem 3.2rem rgba(47, 117, 224, 0.1);
  backdrop-filter: blur(0.6rem);
  cursor: pointer;
  transition:
    transform 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 50%;
    height: 100%;
    background: linear-gradient(
      110deg,
      rgba(255, 255, 255, 0.48),
      rgba(255, 255, 255, 0.08) 48%,
      rgba(55, 164, 255, 0.06)
    );
    transform: skewX(-18deg) translateX(-18%);
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-0.4rem);
    border-color: rgba(54, 116, 254, 0.58);
    background: rgba(255, 255, 255, 0.82);
    box-shadow: 0 2rem 5rem rgba(54, 116, 254, 0.16);
  }

  h3 {
    position: relative;
    z-index: 1;
    margin: 0 0 0.6rem;
    color: #172033;
    font-size: 1.9rem;
    line-height: 1.45;
  }

  p {
    position: relative;
    z-index: 1;
    margin: 0;
    color: #667085;
    font-size: 1.45rem;
    line-height: 1.58;
  }
}

.section {
  padding: 9.6rem 0;

  &.soft {
    background: #f6f9fc;
  }

  &.blue {
    color: #ffffff;
    background: linear-gradient(
      135deg,
      rgba(16, 85, 197, 0.98) 0%,
      rgba(54, 116, 254, 0.94) 58%,
      rgba(22, 52, 118, 0.98) 100%
    );
  }
}

.section-head {
  max-width: 102rem;
  margin: 0 auto 5.6rem;
  text-align: center;

  h2 {
    margin: 0 0 1.8rem;
    color: #172033;
    font-size: 4.2rem;
    font-weight: 700;
    line-height: 1.34;
  }

  p {
    margin: 0 auto;
    color: #666a77;
    font-size: 2rem;
    line-height: 1.6;
  }

  &.light {
    max-width: 98rem;

    .section-kicker,
    h2,
    p {
      color: #ffffff;
    }

    p {
      opacity: 0.9;
    }
  }
}

.formula-title,
.bayes-title {
  > span {
    display: block;
  }
}

.term-note {
  position: relative;
  display: inline-block;
  color: inherit;
  white-space: nowrap;
}

.term-note-icon {
  position: absolute;
  top: -0.6em;
  right: -0.72em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.1rem;
  height: 1.1rem;
  color: #ffffff;
  font-size: 0.8rem;
  font-style: normal;
  font-weight: 700;
  line-height: 1;
  background: #3674fe;
  border-radius: 50%;
  box-shadow: 0 0.2rem 0.6rem rgba(54, 116, 254, 0.28);
  cursor: help;
  transform: scale(0.86);
}

.journey {
  display: grid;
  gap: 2.8rem;
}

.journey-step {
  display: grid;
  grid-template-columns: minmax(46rem, 0.95fr) minmax(0, 1.05fr);
  gap: 4.2rem;
  align-items: center;
  padding: 3rem;
  border: 0.1rem solid rgba(226, 235, 246, 0.9);

  &.reverse {
    grid-template-columns: minmax(0, 1.05fr) minmax(46rem, 0.95fr);

    .journey-media {
      order: 2;
    }
  }
}

.journey-media,
.engine-showcase {
  min-width: 0;

  .screen {
    width: 100%;
    height: 31rem;
    background: #ffffff;
    box-shadow: 0 1.6rem 4rem rgba(78, 119, 169, 0.16);
  }
}

.image-title-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  margin-bottom: 1.2rem;

  button {
    min-width: 10rem;
    height: 3.6rem;
    padding: 0 1.6rem;
    color: #667085;
    font-size: 1.5rem;
    font-weight: 600;
    border: 0.1rem solid #e2e8f0;
    border-radius: 1.8rem;
    background: #ffffff;
    cursor: pointer;

    &.active,
    &:hover {
      color: #ffffff;
      border-color: #3674fe;
      background: #3674fe;
    }
  }

  &.compact {
    justify-content: center;

    button {
      min-width: 8.6rem;
    }
  }
}

.journey-copy {
  position: relative;
  padding: 5.6rem 1.2rem 1.8rem 2.8rem;

  .step-num {
    position: absolute;
    top: 1.8rem;
    left: 2.8rem;
    color: rgba(54, 116, 254, 0.1);
    font-family: DINAlternate, Arial, sans-serif;
    font-size: 8.8rem;
    font-weight: 700;
    line-height: 0.82;
  }

  h3 {
    position: relative;
    z-index: 1;
    margin: 0 0 1.4rem;
    color: #172033;
    font-size: 2.6rem;
    line-height: 1.35;
  }

  p {
    position: relative;
    z-index: 1;
    margin: 0;
    color: #666a77;
    font-size: 1.6rem;
    line-height: 1.9;
  }
}

.bayes-tabs {
  width: 100%;
}

.bayes-tab-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.6rem;
  margin-bottom: 1.8rem;
  padding: 0.6rem;
  border: 0.1rem solid rgba(255, 255, 255, 0.38);
  background: rgba(255, 255, 255, 0.14);

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.8rem;
    min-width: 0;
    min-height: 5.6rem;
    padding: 1.2rem 1rem;
    color: rgba(255, 255, 255, 0.84);
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 1.45;
    text-align: center;
    white-space: nowrap;
    border: 0;
    background: transparent;
    cursor: pointer;

    span {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2.8rem;
      height: 2.8rem;
      flex: 0 0 auto;
      color: #ffffff;
      font-family: DINAlternate, Arial, sans-serif;
      font-size: 1.5rem;
      background: rgba(255, 255, 255, 0.18);
      border-radius: 50%;
    }

    &.active,
    &:hover {
      color: #3674fe;
      background: #ffffff;
      box-shadow: 0 1rem 2.6rem rgba(13, 75, 177, 0.18);

      span {
        background: linear-gradient(318deg, #0f81f4 0%, #37a4ff 100%);
      }
    }
  }
}

.bayes-panel {
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(36rem, 1.08fr);
  gap: 2.8rem;
  align-items: stretch;
  padding: 2.4rem;
  border: 0.1rem solid rgba(255, 255, 255, 0.36);
  background: rgba(255, 255, 255, 0.12);

  .screen {
    width: 100%;
    min-height: 30rem;
    background: #ffffff;
  }
}

.bayes-panel-copy {
  align-self: center;

  h3 {
    margin: 0 0 1.4rem;
    color: #ffffff;
    font-size: 2.4rem;
    line-height: 1.35;
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.88);
    font-size: 1.6rem;
    line-height: 1.85;
  }
}

.support-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.8rem;
}

.support-item {
  min-height: 15.4rem;
  padding: 2rem 2.2rem;
  background: #ffffff;
  border: 0.1rem solid #e6eef8;
  box-shadow: 0 1.6rem 3.6rem rgba(78, 119, 169, 0.08);

  h3 {
    margin: 0 0 0.8rem;
    color: #172033;
    font-size: 2.1rem;
    line-height: 1.4;
  }

  p {
    margin: 0;
    color: #667085;
    font-size: 1.5rem;
    line-height: 1.75;
  }
}

.engine-lanes {
  display: grid;
  gap: 3.8rem;
  margin-top: 4.6rem;
}

.lane {
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(40rem, 0.9fr);
  gap: 4.2rem;
  align-items: center;
  padding: 3.2rem 0;

  &.lane-reverse {
    grid-template-columns: minmax(40rem, 0.9fr) minmax(0, 0.92fr);

    .lane-copy {
      grid-column: 2;
    }

    .engine-showcase {
      grid-column: 1;
      grid-row: 1;
    }
  }
}

.lane-copy {
  h3 {
    margin: 0 0 1.6rem;
    color: #172033;
    font-size: 2.9rem;
    line-height: 1.35;
  }

  p {
    margin: 0 0 2.4rem;
    color: #596276;
    font-size: 1.9rem;
    line-height: 1.65;
  }
}

.feature-list {
  display: grid;
  gap: 1.4rem;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    position: relative;
    padding-left: 2.4rem;
    color: #667085;
    font-size: 1.7rem;
    line-height: 1.72;

    &::before {
      content: '';
      position: absolute;
      top: 0.8rem;
      left: 0;
      width: 0.8rem;
      height: 0.8rem;
      background: #3674fe;
      border-radius: 50%;
    }
  }

  strong {
    display: block;
    margin-bottom: 0.4rem;
    color: #172033;
    font-size: 1.8rem;
  }
}

.engine-showcase {
  position: relative;

  .screen {
    height: 34rem;
  }
}

.spectrum-showcase {
  display: grid;
  grid-template-columns: minmax(42rem, 0.96fr) minmax(0, 1.04fr);
  gap: 5.2rem;
  align-items: center;
  margin-top: 5.6rem;
}

.spectrum-visual-panel {
  min-width: 0;

  .image-title-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 0.8rem;
    margin-bottom: 1.2rem;

    button {
      min-width: 10rem;
      height: 3.6rem;
      padding: 0 1.6rem;
      color: #667085;
      font-size: 1.5rem;
      font-weight: 600;
      border: 0.1rem solid #e2e8f0;
      border-radius: 1.8rem;
      background: #ffffff;
      cursor: pointer;

      &.active,
      &:hover {
        color: #ffffff;
        border-color: #3674fe;
        background: #3674fe;
      }
    }
  }

  .screen {
    width: 100%;
    height: 42rem;
    background: #ffffff;
    box-shadow: 0 1.8rem 4.4rem rgba(78, 119, 169, 0.15);
  }
}

.spectrum-copy-panel {
  .numbered {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2.6rem 2.8rem;
    counter-reset: spectrum-feature;

    li {
      counter-increment: spectrum-feature;
      padding-left: 0;
      line-height: 1.62;

      &::before {
        content: none;
      }

      &:last-child {
        grid-column: span 2;
      }
    }

    strong {
      position: relative;
      z-index: 1;
      min-height: 3.6rem;
      margin-bottom: 0.2rem;
      padding-top: 1.2rem;

      &::before {
        content: counter(spectrum-feature, decimal-leading-zero);
        position: absolute;
        top: 0;
        left: 0;
        z-index: -1;
        color: rgba(54, 116, 254, 0.11);
        font-family: DINAlternate, Arial, sans-serif;
        font-size: 4.6rem;
        font-weight: 700;
        line-height: 0.8;
      }
    }
  }
}

.partner-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 2.4rem;

  .partner {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 17.2rem;
    overflow: hidden;
    padding: 1.2rem;
    background: #ffffff;
    border: 0.1rem solid #e6eef8;
    box-shadow: 0 1.2rem 3rem rgba(78, 119, 169, 0.08);

    ::v-deep .el-image {
      width: 100%;
      height: 100%;
    }

    ::v-deep .el-image__inner {
      transform: scale(var(--partner-logo-scale, 1.6));
      transform-origin: center;
      transition: transform 0.2s ease;
    }

    &:hover {
      ::v-deep .el-image__inner {
        transform: scale(calc(var(--partner-logo-scale, 1.6) + 0.04));
      }
    }
  }
}

.industry-tabs {
  width: 100%;
  padding: 1rem;
  background: #ffffff;
  border: 0.1rem solid #e6eef8;
  box-shadow: 0 1.6rem 3.8rem rgba(78, 119, 169, 0.06);
}

.industry-tab-list {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0;
  border: 0.1rem solid rgba(54, 116, 254, 0.16);
  background: rgba(54, 116, 254, 0.04);

  button {
    display: grid;
    grid-template-columns: 3.2rem minmax(0, 1fr);
    grid-template-rows: auto auto;
    align-items: center;
    gap: 0.2rem 1rem;
    min-width: 0;
    min-height: 7.2rem;
    padding: 1.3rem 1.4rem;
    color: #596276;
    line-height: 1.45;
    text-align: left;
    border: 0;
    background: transparent;
    cursor: pointer;
    transition:
      color 0.2s ease,
      background 0.2s ease,
      box-shadow 0.2s ease;

    span {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      grid-row: span 2;
      width: 3.2rem;
      height: 3.2rem;
      color: #3674fe;
      font-family: DINAlternate, Arial, sans-serif;
      font-size: 1.5rem;
      background: rgba(54, 116, 254, 0.1);
      border-radius: 50%;
      transition:
        color 0.2s ease,
        background 0.2s ease;
    }

    strong {
      min-width: 0;
      overflow: hidden;
      color: #172033;
      font-size: 1.6rem;
      font-weight: 700;
      text-overflow: ellipsis;
      white-space: nowrap;
      transition: color 0.2s ease;
    }

    small {
      min-width: 0;
      overflow: hidden;
      color: #8b93a6;
      font-size: 1.3rem;
      font-weight: 600;
      text-overflow: ellipsis;
      white-space: nowrap;
      transition: color 0.2s ease;
    }

    &.active,
    &:hover {
      color: #ffffff;
      background: linear-gradient(318deg, #0f81f4 0%, #37a4ff 100%);
      box-shadow: 0 1rem 2.6rem rgba(13, 75, 177, 0.18);

      span {
        color: #3674fe;
        background: #ffffff;
      }

      strong,
      small {
        color: #ffffff;
      }
    }
  }
}

.industry-panel {
  background:
    linear-gradient(
      180deg,
      rgba(54, 116, 254, 0.035) 0%,
      rgba(255, 255, 255, 0) 22%
    ),
    #ffffff;
  border: 0.1rem solid #e6eef8;
}

.industry-body {
  display: grid;
  gap: 2.4rem;
  padding: 3rem;
}

.industry-detail {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(48rem, 1fr);
  gap: 2.8rem;
  align-items: stretch;
}

.industry-scenarios {
  min-width: 0;
}

.industry-media {
  display: flex;
  align-items: center;
  min-width: 0;
}

.industry-image-card {
  position: sticky;
  top: 9rem;
  width: 100%;
  padding: 1.2rem;
  background: #ffffff;
  border: 0.1rem solid #e6eef8;
  box-shadow: 0 1.6rem 3.8rem rgba(78, 119, 169, 0.1);

  .screen {
    width: 100%;
    height: 42rem;
    background: #f9fbfd;
  }

  ::v-deep .el-image__inner {
    display: block;
  }
}

.industry-traits {
  max-width: 120rem;
  margin: 0 auto;
  padding: 1.4rem 3.4rem 2.4rem;
  text-align: center;

  h3 {
    display: inline-flex;
    align-items: center;
    gap: 1.2rem;
    margin: 0 0 1.4rem;
    color: #172033;
    font-size: 2.2rem;
    font-weight: 700;
    line-height: 1.4;

    &::before,
    &::after {
      content: '';
      width: 3.6rem;
      height: 0.2rem;
      background: linear-gradient(90deg, rgba(54, 116, 254, 0), #3674fe);
      border-radius: 0.2rem;
    }

    &::after {
      background: linear-gradient(90deg, #3674fe, rgba(54, 116, 254, 0));
    }
  }

  p {
    margin: 0;
    color: #596276;
    font-size: 1.6rem;
    line-height: 1.82;
  }
}

.industry-section {
  padding: 0;

  h3 {
    display: flex;
    align-items: center;
    gap: 1.6rem;
    flex-wrap: wrap;
    margin: 0 0 1.4rem;
    color: #172033;
    font-size: 2rem;
    font-weight: 700;
    line-height: 1.4;
  }

  p {
    margin: 0;
    color: #596276;
    font-size: 1.5rem;
    line-height: 1.76;
  }
}

.industry-products-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.4rem 1.4rem;
  color: #3674fe;
  font-size: 1.3rem;
  font-weight: 600;
  line-height: 1.6;
  background: rgba(54, 116, 254, 0.06);
  border: 0.1rem solid rgba(54, 116, 254, 0.18);
  border-radius: 2rem;
  white-space: nowrap;
}

.scenario-list {
  display: grid;
  gap: 1.2rem;
}

.scenario-card {
  position: relative;
  padding: 0 0 1.4rem 2rem;
  background: transparent;
  border: 0;
  border-bottom: 0.1rem solid #edf2f8;
  transition:
    border-color 0.2s ease,
    transform 0.2s ease;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0.85rem;
    width: 0.8rem;
    height: 0.8rem;
    background: #3674fe;
    border-radius: 50%;
  }

  &:hover {
    transform: translateX(0.4rem);
    border-color: rgba(54, 116, 254, 0.24);
  }

  h4 {
    margin: 0 0 0.6rem;
    color: #172033;
    font-size: 1.7rem;
    font-weight: 700;
    line-height: 1.45;
  }

  p {
    margin: 0;
    color: #596276;
    font-size: 1.46rem;
    line-height: 1.72;
  }
}

.faq-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2rem;
}

.faq {
  display: grid;
  grid-template-columns: 5.6rem minmax(0, 1fr);
  gap: 0 1.6rem;
  min-height: 16rem;
  padding: 2.4rem;
  background: #ffffff;
  border: 0.1rem solid #e6eef8;
  box-shadow: 0 1.6rem 3.8rem rgba(78, 119, 169, 0.08);

  .faq-num {
    grid-row: span 2;
    color: #3674fe;
    font-family: DINAlternate, Arial, sans-serif;
    font-size: 3.4rem;
    font-weight: 700;
    line-height: 1;
  }

  h3 {
    margin: 0 0 1rem;
    color: #172033;
    font-size: 2rem;
    line-height: 1.48;
  }

  p {
    margin: 0;
    color: #667085;
    font-size: 1.5rem;
    line-height: 1.76;
  }
}

@import "@/styles/mixins.scss";

@include mobile {
  .floating-nav {
    display: none;
  }
  .wrap,
  .wrap.narrow {
    width: 100%;
    max-width: 100%;
    padding: 0 1.6rem;
  }
  .image-title-tabs,
  .bayes-tab-list,
  .industry-tab-list {
    display: none !important;
  }
  ::v-deep .mobile-tab-fold {
    padding: 0;
    margin: 0 0 1.6rem;
  }
  .section.blue ::v-deep .mobile-tab-fold {
    .tab-row {
      border-bottom-color: rgba(255, 255, 255, 0.28);
    }
    .tab-item {
      color: rgba(255, 255, 255, 0.78);
      &.active {
        color: #ffffff;
        box-shadow: inset 0 -0.2rem 0 #ffffff;
      }
    }
    .more-btn i {
      color: #ffffff;
    }
  }
  .hero-process,
  .hero-process .hero-shade {
    min-height: 32rem;
  }
  .hero-process .hero-shade {
    padding: 6.4rem 0 3.2rem;
  }
  .hero-copy {
    max-width: 100%;
    h1 {
      font-size: 2.6rem;
      span,
      .title-accent {
        white-space: normal !important;
      }
    }
    .lead {
      width: 100%;
      max-width: 100%;
      font-size: 1.6rem;
      white-space: normal !important;
    }
    .hero-tags li {
      white-space: normal !important;
    }
  }
  .section {
    padding: 4.8rem 0;
  }
  .section-head {
    margin-bottom: 2.4rem;
    h2 {
      font-size: 2.4rem;
      margin-bottom: 1.2rem;
    }
    p {
      font-size: 1.4rem;
    }
  }
  .journey-step,
  .journey-step.reverse {
    grid-template-columns: 1fr;
    gap: 1.6rem;
    padding: 1.6rem;
    .journey-media {
      order: 0;
    }
  }
  .journey-copy {
    padding: 0.4rem 0 0;
    .step-num {
      position: static;
      display: block;
      margin-bottom: 0.4rem;
      font-size: 3.2rem;
    }
  }
  .journey-media .screen,
  .engine-showcase .screen,
  .spectrum-visual-panel .screen,
  .bayes-panel .screen,
  .industry-image-card .screen {
    height: auto;
    min-height: 0;
    ::v-deep .el-image__inner {
      position: static;
      width: 100%;
      height: auto;
    }
  }
  .bayes-tab-list,
  .bayes-panel,
  .support-grid,
  .spectrum-showcase,
  .industry-detail,
  .industry-tab-list {
    grid-template-columns: 1fr;
  }
  .partner-grid {
    grid-template-columns: 1fr 1fr;
    grid-auto-rows: 1fr;
    align-items: stretch;
    gap: 1.2rem;
    .partner {
      height: 10rem;
      padding: 1rem;
    }
  }
  .support-grid {
    gap: 1.2rem;
  }
  .support-item {
    min-height: 0;
    padding: 1.4rem 1.6rem;
    h3 {
      font-size: 1.8rem;
      margin-bottom: 0.6rem;
    }
    p {
      font-size: 1.4rem;
      line-height: 2.2rem;
    }
  }
  .industry-tab-list button {
    min-height: 0;
    padding: 1.2rem 1.4rem;
    strong,
    small {
      white-space: normal;
      overflow: visible;
      text-overflow: unset;
    }
  }
  .lane,
  .lane.lane-reverse {
    grid-template-columns: 1fr;
    gap: 1.6rem;
    padding: 1.6rem 0;
    .lane-copy,
    .engine-showcase {
      grid-column: auto;
      grid-row: auto;
    }
  }
  .faq-grid {
    grid-template-columns: 1fr;
  }
  .process-hero-grid {
    grid-template-columns: 1fr !important;
  }
}
</style>

<style lang="scss">
.term-tooltip-popper {
  max-width: 34rem;
  padding: 1rem 1.2rem;
  color: #293244;
  font-size: 1.3rem;
  line-height: 1.65;
  border-color: #dbe7f6;
  box-shadow: 0 1rem 3rem rgba(78, 119, 169, 0.16);
}
</style>
